import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import API_URL from '../config';
import { parseDateSafe, toCalendarDateKey } from '../utils/dateUtils';
import { useAuth } from '../AuthContext';
import { getStoredToken, getStoredUserId } from '../utils/auth';

const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const hours = Array.from({ length: 19 }, (_, i) => `${(6 + i).toString().padStart(2, "0")}:00`);
const hourSlotHeight = 66.66;
const pixelsPerMinute = hourSlotHeight / 60;

const formatDuration = (totalMinutes) => `${Math.floor(totalMinutes / 60)}h`;

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - ((day + 6) % 7);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDateRange = (startDate) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  return `${startDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${endDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`;
};

export default function MonPlanningHebdo() {
  const { user, token: ctxToken } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [weekStart, setWeekStart] = useState(getStartOfWeek(new Date()));
  const [contextMenu, setContextMenu] = useState(null);
  const [requestModal, setRequestModal] = useState({ open: false, shift: null, startTime: "", endTime: "" });

  const token = ctxToken || getStoredToken();
  const userId = user?.id || getStoredUserId();
  const SHIFTS_API = `${API_URL}/shifts`;
  const REQUESTS_API = `${API_URL}/requests`;

  useEffect(() => {
    fetchMyShifts();
    fetchMyRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const fetchMyShifts = async () => {
    if (!userId) {
      console.warn("user_id manquant dans le localStorage");
      return;
    }
    try {
      const res = await fetch(`${SHIFTS_API}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erreur lors du chargement de mes shifts");
      const data = await res.json();
      setShifts(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const res = await fetch(`${REQUESTS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.warn("Impossible de charger les demandes");
        setRequests([]);
        return;
      }
      const data = await res.json();
      setRequests(data);
    } catch (err) {
      console.error("Erreur lors du chargement des demandes:", err);
      setRequests([]);
    }
  };

  const handleWeekChange = (direction) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + direction * 7);
    setWeekStart(getStartOfWeek(newDate));
  };

  const resetToToday = () => setWeekStart(getStartOfWeek(new Date()));

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const today = new Date();

  const handleRightClick = (e, shift) => {
    e.preventDefault();
    const menuWidth = 220;
    const menuHeight = 90;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth - 10);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight - 10);
    setContextMenu({ x, y, shift });
  };

  const handleOption = async (action) => {
    if (!contextMenu) return;
    const { shift } = contextMenu;

    if (action === "request") {
      const existingRequest = requests.find(
        req => req.shift_id === shift.id && req.status === "pending"
      );
      
      if (existingRequest) {
        alert("Une demande est déjà en attente pour ce shift.");
        setContextMenu(null);
        return;
      }

      setRequestModal({
        open: true,
        shift,
        startTime: shift.start_time.slice(11, 16),
        endTime: shift.end_time.slice(11, 16),
      });
      setContextMenu(null);
      return;
    }

    setContextMenu(null);
  };

  const handleRequestSubmit = async () => {
    const { shift, startTime, endTime } = requestModal;
    
    if (endTime <= startTime) {
      alert("L'heure de fin doit être postérieure à l'heure de début.");
      return;
    }

    try {
      const payload = {
        shift_id: shift.id,
        start_time: `${toCalendarDateKey(shift.start_time)}T${startTime}:00`,
        end_time: `${toCalendarDateKey(shift.end_time)}T${endTime}:00`,
      };
      
      const res = await fetch(`${REQUESTS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Erreur lors de la création de la demande");
      }
      
      await fetchMyRequests();
      setRequestModal({ open: false, shift: null, startTime: "", endTime: "" });
      alert("Demande de modification créée avec succès !");
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  const getPendingRequestForShift = (shiftId) => {
    return requests.find(req => req.shift_id === shiftId && req.status === "pending");
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800" onClick={() => setContextMenu(null)}>
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Mon planning</h1>
            <p className="text-sm text-gray-500">{formatDateRange(weekStart)}</p>
          </div>
          <div className="flex items-center space-x-2 text-gray-500">
            <button onClick={() => handleWeekChange(-1)}><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={resetToToday} className="text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded">Aujourd'hui</button>
            <button onClick={() => handleWeekChange(1)}><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Planning Grid */}
        <div className="overflow-hidden border rounded-xl bg-white shadow-sm relative">
          {/* Header Days */}
          <div className="grid grid-cols-8 border-b text-sm text-gray-600 font-medium">
            <div className="p-3 bg-gray-50"></div>
            {days.map((day, i) => {
              const currentDate = new Date(weekStart);
              currentDate.setDate(currentDate.getDate() + i);
              const isToday = isSameDay(currentDate, today);
              return (
                <div key={day} className={`p-3 text-center ${isToday ? "bg-indigo-50 font-semibold text-indigo-700" : ""}`}>
                  {day}
                </div>
              );
            })}
          </div>

          {/* Shifts */}
          <div className="grid grid-cols-8 relative text-xs h-[1266px]">
            <div className="border-r bg-gray-50 text-right text-gray-400">
              {hours.map((hour) => (
                <div key={hour} className="h-[66.66px] pr-3 pt-1">{hour}</div>
              ))}
            </div>

            {days.map((day, i) => {
              const currentDate = new Date(weekStart);
              currentDate.setDate(currentDate.getDate() + i);
              const isToday = isSameDay(currentDate, today);

              return (
                <div key={day} className={`border-r relative ${isToday ? "bg-indigo-50/30" : ""}`}>
                  {shifts
                    .filter((shift) => isSameDay(parseDateSafe(shift.start_time), currentDate))
                    .map((shift, index) => {
                      const start = parseDateSafe(shift.start_time);
                      const end = parseDateSafe(shift.end_time);
                      const top = (start.getHours() * 60 + start.getMinutes() - 360) * pixelsPerMinute;
                      const height = (end - start) / (1000 * 60) * pixelsPerMinute;
                      const duration = formatDuration((end - start) / 60000);
                      
                      const pendingRequest = getPendingRequestForShift(shift.id);

                      return (
                        <div
                          key={shift.id || index}
                          onContextMenu={(e) => handleRightClick(e, shift)}
                          className={`absolute left-2 right-2 rounded-xl p-2 text-white text-xs shadow-md flex flex-col justify-between cursor-pointer ${
                            pendingRequest 
                              ? "bg-orange-500 hover:bg-orange-600 border-2 border-orange-300" 
                              : "bg-indigo-500 hover:bg-indigo-600"
                          }`}
                          style={{ top, height }}
                        >
                          <div className="font-semibold flex items-center justify-between">
                            <span>{shift.label || "Shift"}</span>
                            {pendingRequest && <span className="text-xs">⏳</span>}
                          </div>
                          <div className="text-[10px] opacity-90 flex justify-between items-center">
                            <span>{shift.start_time.slice(11, 16)} - {shift.end_time.slice(11, 16)}</span>
                          </div>
                          
                          {/* Affiche la demande en attente */}
                          {pendingRequest && (
                            <div className="text-[10px] bg-white/20 rounded px-1 py-0.5 mt-1">
                              <span>Demande : </span>
                              <span>{pendingRequest.start_time.slice(11, 16)} - {pendingRequest.end_time.slice(11, 16)}</span>
                            </div>
                          )}
                          
                          <div className="text-[10px] text-right">{duration}</div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>

          {/* Context Menu */}
          {contextMenu && (
            <div
              className="absolute bg-white border rounded shadow-md z-50 w-64"
              style={{
                top: contextMenu.y,
                left: contextMenu.x,
                position: "fixed",
              }}
            >
              <button onClick={() => handleOption("request")} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">
                📝 Demander une modification d'horaires
              </button>
            </div>
          )}

          {/* Modale de création de demande */}
          {requestModal.open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-96 shadow-lg">
                <h2 className="text-lg font-semibold mb-4">Demander une modification</h2>
                
                <p className="text-sm text-gray-600 mb-4">
                  <span>Shift actuel : </span>
                  <span className="font-medium">
                    {requestModal.shift ? `${requestModal.shift.start_time.slice(11, 16)} - ${requestModal.shift.end_time.slice(11, 16)}` : ""}
                  </span>
                </p>

                <label className="block text-sm mb-2 font-medium">Nouvelle heure de début</label>
                <input
                  type="time"
                  value={requestModal.startTime || ""}
                  onChange={(e) => setRequestModal(prev => ({ ...prev, startTime: e.target.value }))}
                  className="border rounded w-full p-2 mb-4"
                  autoComplete="off"
                />

                <label className="block text-sm mb-2 font-medium">Nouvelle heure de fin</label>
                <input
                  type="time"
                  value={requestModal.endTime || ""}
                  onChange={(e) => setRequestModal(prev => ({ ...prev, endTime: e.target.value }))}
                  className="border rounded w-full p-2 mb-6"
                  autoComplete="off"
                />

                <div className="flex justify-end space-x-3">
                  <button 
                    onClick={() => setRequestModal({ open: false, shift: null, startTime: "", endTime: "" })} 
                    className="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button 
                    onClick={handleRequestSubmit} 
                    className="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Soumettre la demande
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
